/**
 * Honest threat-tier engine — reconciled to the Claude Design review's contract
 * (designs/biomax-epi/biomax-review/mock-data.js). colour=threat is an ASSERTION
 * of danger, so the tier is DERIVED from real catalog data, never hand-ranked.
 *
 * Severity × transmissibility matrix → 5 tiers:
 *   severityClass(IFR): ≥.30 → 3 · ≥.05 → 2 · ≥.01 → 1 · else 0
 *   transClass(R0):     ≥5   → 2 · ≥2   → 1 · else 0
 *        sv≥2 │ tx≥1 CRITICAL · tx0 HIGH
 *        sv=1 │ tx≥1 HIGH     · tx0 ELEVATED
 *        sv=0 │ tx≥1 ELEVATED · tx0 WATCH
 *
 * Honesty fold-ins (mirror the feed's rules so the single tier field is enough to
 * drive colour): genomic sampling → WATCH (context, not incidence); stale data →
 * ROUTINE (demoted regardless of score); an unverified signal can never assert
 * CRITICAL (caps at HIGH until corroborated).
 *
 * Inputs are deliberately REPRESENTATIVE, not worst-case: IFR = the MEDIAN across
 * the catalog's ifrByAgeBand (the max elderly band would over-tier everything);
 * R0 = the catalog's upper estimate (R0.high). Calibrated against the live catalog
 * 2026-05-29: Ebola/Marburg CRITICAL · H5N1/Nipah/MERS/Lassa/SARS-CoV-2/cholera
 * HIGH · mpox/measles/dengue/RSV ELEVATED · seasonal flu WATCH.
 */

import { findPathogen } from '@biomax/plugin-pathogen-database';
import type { Signal } from '@biomax/plugin-surveillance-feed';

export type ThreatTier = 'routine' | 'watch' | 'elevated' | 'high' | 'critical';

export const THREAT_TIER_META: Record<ThreatTier, { label: string; color: string; bg: string; rank: number }> = {
  critical: { label: 'CRITICAL', color: 'var(--bm-threat-1)', bg: 'var(--bm-threat-1-bg)', rank: 4 },
  high:     { label: 'HIGH',     color: 'var(--bm-threat-2)', bg: 'var(--bm-threat-2-bg)', rank: 3 },
  elevated: { label: 'ELEVATED', color: 'var(--bm-threat-3)', bg: 'var(--bm-threat-3-bg)', rank: 2 },
  watch:    { label: 'WATCH',    color: 'var(--bm-threat-4)', bg: 'var(--bm-threat-4-bg)', rank: 1 },
  routine:  { label: 'ROUTINE',  color: 'var(--bm-threat-0)', bg: 'var(--bm-threat-0-bg)', rank: 0 },
};

function severityClass(ifr: number): number { return ifr >= 0.30 ? 3 : ifr >= 0.05 ? 2 : ifr >= 0.01 ? 1 : 0; }
function transClass(r0: number): number { return r0 >= 5 ? 2 : r0 >= 2 ? 1 : 0; }
function baseTier(sv: number, tx: number): ThreatTier {
  if (sv >= 2) return tx >= 1 ? 'critical' : 'high';
  if (sv === 1) return tx >= 1 ? 'high' : 'elevated';
  return tx >= 1 ? 'elevated' : 'watch';
}

export interface TierResult {
  tier: ThreatTier;
  ifr: number | null;
  r0: number | null;
  reason: 'severity' | 'genomic' | 'stale' | 'unknown';
  cappedByVerification: boolean;
}

export interface TierFlags { confirmed: boolean; stale?: boolean; genomic?: boolean }

/** Pure core — testable without the catalog. */
export function threatTierFromInputs(ifr: number | null, r0: number | null, flags: TierFlags): TierResult {
  if (flags.genomic) return { tier: 'watch', ifr, r0, reason: 'genomic', cappedByVerification: false };
  if (flags.stale)   return { tier: 'routine', ifr, r0, reason: 'stale', cappedByVerification: false };
  if (ifr == null || r0 == null) return { tier: 'watch', ifr, r0, reason: 'unknown', cappedByVerification: false };
  let tier = baseTier(severityClass(ifr), transClass(r0));
  let capped = false;
  if (!flags.confirmed && tier === 'critical') { tier = 'high'; capped = true; }
  return { tier, ifr, r0, reason: 'severity', cappedByVerification: capped };
}

/** Median IFR across reported age bands — representative, not the worst-case band. */
function representativeIfr(bands: { ifr?: number }[] | undefined): number | null {
  const xs = (bands ?? []).map(b => b.ifr).filter((x): x is number => typeof x === 'number').sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = xs.length >> 1;
  return xs.length % 2 ? xs[mid]! : (xs[mid - 1]! + xs[mid]!) / 2;
}

function catalogVitals(pathogenId: string): { ifr: number | null; r0: number | null } {
  const p = findPathogen(pathogenId) as
    { R0?: { value?: number; high?: number }; ifrByAgeBand?: { ifr?: number }[] } | undefined;
  return { ifr: representativeIfr(p?.ifrByAgeBand), r0: p?.R0?.high ?? p?.R0?.value ?? null };
}

/** Intrinsic catalog severity (ignores verification) — drives the legend matrix. */
export function pathogenSeverity(pathogenId: string): TierResult {
  const { ifr, r0 } = catalogVitals(pathogenId);
  return threatTierFromInputs(ifr, r0, { confirmed: true });
}

/** Per-signal threat tier — catalog severity, with this signal's honesty fold-ins. */
export function signalThreatTier(
  s: Pick<Signal, 'pathogenId' | 'verificationStatus' | 'metric'>,
  opts: { stale?: boolean } = {},
): TierResult {
  const { ifr, r0 } = catalogVitals(s.pathogenId);
  return threatTierFromInputs(ifr, r0, {
    confirmed: s.verificationStatus === 'confirmed',
    stale: opts.stale,
    genomic: s.metric === 'sequences' || s.metric === 'seq-frequency',
  });
}
